<script lang="ts" setup>
import { computed, ref } from "vue";

import { intervalToDuration, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { timezoneToParsedOffset, minTwoDigits } from "@/utils";

import { ClaimType, EffectiveFlight } from "@/interfaces";
import { ClockIcon, QuestionMarkCircleIcon } from "@heroicons/vue/24/solid";

const props = defineProps<{
  claimTypeName: any;
  monitoringStatus: string | null;
  scheduledDepartureUTC: Date;
  scheduledArrivalUTC: Date;
  
  actualDepartureUTC?: Date | null;
  inGateArrivalUTC?: Date | null;

  departureAirport: EffectiveFlight["departureAirport"];
  arrivalAirport: EffectiveFlight["arrivalAirport"];
  utc: boolean;
}>();

function formatFlightSchedule(date: Date, timezone: string) {
  const tz = displayUTC.value === true ? "UTC" : timezone;
  return formatInTimeZone(date, tz, 'HH:mm z', { locale: fr });
}

function formatFlightDate(date: Date, timezone: string, format: string = 'dd/MM/yyyy') {
  const tz = displayUTC.value === true ? "UTC" : timezone;
  return formatInTimeZone(date, tz, format, { locale: fr })
}

const departureTimeExpectedFormatted = computed(() =>
  formatFlightSchedule(props.scheduledDepartureUTC, props.departureAirport.timezone));

const departureTimeActualFormatted = computed(() => {
  if (props.monitoringStatus?.startsWith("CANCEL")) return "ANNULÉ"
  if (!props.actualDepartureUTC) return "(aucune donnée)";
  return formatFlightSchedule(props.actualDepartureUTC as Date, props.departureAirport.timezone);
});

const arrivalTimeExpectedFormatted = computed(() =>
  formatFlightSchedule(props.scheduledArrivalUTC, props.arrivalAirport.timezone));

const arrivalTimeActualFormatted = computed(() => {
  if (props.monitoringStatus?.startsWith("CANCEL")) return "ANNULÉ"
  if (!props.inGateArrivalUTC) return "(aucune donnée)"
  return formatFlightSchedule(props.inGateArrivalUTC, props.arrivalAirport.timezone);
});

const isLate = computed(() => {
  return (new Date(props.scheduledArrivalUTC).getTime() - new Date(props.inGateArrivalUTC as Date).getTime()) < 0
})
const interval = computed(() => {
  const interval = {
    start: new Date(props.scheduledArrivalUTC),
    end: new Date(props.inGateArrivalUTC as Date)
  }
  const parsedDelay = intervalToDuration(interval);
  if (parsedDelay.days) {
    parsedDelay.hours ??= 0;
    parsedDelay.hours += parsedDelay.days * 24;
    parsedDelay.days = 0;
  }
  return parsedDelay;
});

const displayUTC = ref(false);
</script>
<template>
<div class="pt-2"
  :class="claimTypeName === 'FLIGHT_CANCELLATION' || claimTypeName === 'PNR_SEGMENT_CANCELLATION' || claimTypeName === 'OVERBOOKING' ? 'border-l-2 border-red-600' : claimTypeName ? 'border-l-2 border-amber-500' : 'border-l-2 border-blue-500'">
  <!-- Horaires prévues -->
  <div>
    <div class="flex flex-row ml-4">
      <p class="text-base font-medium text-zinc-800">
        Horaires prévus {{ displayUTC ? "UTC" : "" }} :
        <span class="text-xs" @click="displayUTC = !displayUTC">
          (<span class="link">{{ displayUTC ? "voir en local" : "voir en UTC" }}</span>)
        </span>
      </p>
    </div>

    <div class="flex flex-row pb-2">
      <div class="flex-2">
        <div class="flex flex-row">
          <div class="flex flex-col items-start mt-2 ml-4">
            <p class="my-1 text-sm">Départ:</p>
          </div>
          <div class="flex flex-col items-start mt-2 ml-2">
            <p class="text-sm font-semibold leading-7 ">
              {{ departureTimeExpectedFormatted }}
              <span class="font-normal text-gray-700">
                le {{formatFlightDate(scheduledDepartureUTC, departureAirport.timezone)}}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div class="flex-2">
        <div class="flex flex-row pb-2">
          <div class="flex flex-col items-start mt-2 ml-4">
            <p class="my-1 text-sm">Arrivée:</p>
          </div>
          <div class="flex flex-col items-start mt-2 ml-2">
            <p class="text-sm font-semibold leading-7">
              {{ arrivalTimeExpectedFormatted }}
              <span class="font-normal text-gray-700">
                le {{formatFlightDate(scheduledArrivalUTC, arrivalAirport.timezone)}}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- <div class="w-4/6 px-10 py-2 mx-auto border-t border-gray-200 border-dashed"></div> -->

  <!-- Horaires réel -->
  <div class="">
    <div class="flex flex-row mb-1 ml-4">
      <p class="inline-flex font-medium leading-4 align-middle cursor-help text-zinc-800" v-tooltip.right="'Via la surveillance des vols'">
        <span class="">
          <!-- <QuestionMarkCircleIcon class="w-4 h-4 m-0 mr-1 text-gray-400" /> -->
          <ClockIcon class="w-4 h-4 m-0 mr-1 text-gray-400" />
        </span>
        <span class="text-base">Horaires réels {{ displayUTC ? "UTC" : "" }} : </span>
        <span class="ml-1 text-xs leading-4" @click="displayUTC = !displayUTC">
          <!-- (<span class="link">{{ displayUTC ? "voir en local" : "voir en UTC" }}</span>) -->
        </span>
      </p>
    </div>

    <div class="flex flex-row pb-2">
      <div class="flex-2">
        <div class="flex flex-row">
          <div class="flex flex-col items-start mt-2 ml-4">
            <p class="my-1 text-sm">Départ:</p>
          </div>
          <div class="flex flex-col items-start mt-2 ml-2">
            <p class="text-sm font-semibold leading-7">
              <pre v-if="monitoringStatus === 'CANCELLED'">annulé</pre>
              <span v-else>
                {{ departureTimeActualFormatted }}
                <span v-if="actualDepartureUTC" class="font-normal text-gray-700">
                  le {{formatFlightDate(actualDepartureUTC, departureAirport.timezone)}}
                </span>
              </span>
            </p>
          </div>
        </div>
      </div>

      <div class="flex-2">
        <div class="flex flex-row pb-2">
          <div class="flex flex-col items-start mt-2 ml-4">
            <p class="my-1 text-sm">Arrivée:</p>
          </div>
          <div class="flex flex-col items-start mt-2 ml-2">
            <p class="text-sm font-semibold leading-7 text-left">
              <pre v-if="monitoringStatus === 'CANCELLED'">annulé</pre>
              <span v-else>{{ arrivalTimeActualFormatted }}</span>
              <span v-if="inGateArrivalUTC  && monitoringStatus !== 'CANCELLED'" class="font-normal text-gray-700">
                le {{formatFlightDate(inGateArrivalUTC, arrivalAirport.timezone)}}
              </span>
              <span v-if="(interval.minutes && (interval.minutes > 15 || interval.hours || interval.days)) && inGateArrivalUTC"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-1"
                :class="isLate ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'"
              >
                {{ isLate ? 'Retardé de ' : 'En avance de ' }} <template v-if="interval.hours"> {{ interval.hours }} heures </template>{{ interval.minutes }} min
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div> 
</div>
</template>